/* START_MODULE_CONTRACT
 * PURPOSE: Утилиты валидации, форматирования и payload накладных расходов (UC-013).
 * SCOPE: Чистые функции преобразования и валидации данных формы overhead.
 * DEPENDS: overheadConstants
 * LINKS: M-FE-PROJECTS
 * ROLE: RUNTIME
 * MAP_MODE: EXPORTS
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * isOverheadHoursValid - проверка часов
 * isRecurrenceValid - проверка правила периодичности
 * formatRecurrenceLabel - человекочитаемая подпись периодичности
 * buildOverheadPayload - payload для API
 * formatOverheadPreviewLabel - превью строки записи в форме
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-14 - formatOverheadPreviewLabel для live-превью в диалоге оверхеда.
 * LAST_CHANGE: 2026-07-03 - BUG_FIX: import path ../../../models/overheadConstants (was ../../).
 * END_CHANGE_SUMMARY
 */

import {
  DAY_OF_WEEK_LABELS,
  OVERHEAD_RECURRENCE_TYPES,
  OVERHEAD_RECURRENCE_TYPE_LABELS,
} from '../../../models/overheadConstants';

/* START_METHOD_isOverheadHoursValid */
/* START_CONTRACT: isOverheadHoursValid
 * PURPOSE: Проверить, что часы — положительное число в диапазоне (0; 10000].
 * INPUTS: { value: string|null }.
 * OUTPUTS: { boolean }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-PROJECTS
 * END_CONTRACT: isOverheadHoursValid
 */
export function isOverheadHoursValid(value) {
  if (value === '' || value == null) {
    return false;
  }
  const num = parseFloat(value);
  return !Number.isNaN(num) && num > 0 && num <= 10000;
}
/* END_METHOD_isOverheadHoursValid */

/* START_METHOD_isRecurrenceValid */
/* START_CONTRACT: isRecurrenceValid
 * PURPOSE: Проверить согласованность полей периодичности в форме.
 * INPUTS: { recurrenceType, intervalDays, daysOfWeek }.
 * OUTPUTS: { boolean }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-PROJECTS, M-OVERHEAD-RECURRENCE
 * END_CONTRACT: isRecurrenceValid
 */
export function isRecurrenceValid({ recurrenceType, intervalDays, daysOfWeek }) {
  if (!recurrenceType) {
    return false;
  }

  if (recurrenceType === OVERHEAD_RECURRENCE_TYPES.DAILY) {
    return true;
  }

  if (recurrenceType === OVERHEAD_RECURRENCE_TYPES.INTERVAL) {
    const n = parseInt(String(intervalDays), 10);
    return !Number.isNaN(n) && n >= 1 && n <= 365;
  }

  if (recurrenceType === OVERHEAD_RECURRENCE_TYPES.WEEKLY) {
    return Array.isArray(daysOfWeek) && daysOfWeek.length > 0;
  }

  return false;
}
/* END_METHOD_isRecurrenceValid */

/* START_METHOD_formatRecurrenceLabel */
/* START_CONTRACT: formatRecurrenceLabel
 * PURPOSE: Построить краткую подпись правила периодичности.
 * INPUTS: { recurrenceType, intervalDays, daysOfWeek }.
 * OUTPUTS: { string }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-PROJECTS
 * END_CONTRACT: formatRecurrenceLabel
 */
export function formatRecurrenceLabel({ recurrenceType, intervalDays, daysOfWeek }) {
  if (recurrenceType === OVERHEAD_RECURRENCE_TYPES.INTERVAL) {
    const n = intervalDays ?? '';
    return `каждые ${n} дн.`;
  }

  if (recurrenceType === OVERHEAD_RECURRENCE_TYPES.WEEKLY) {
    const labels = (daysOfWeek ?? [])
      .slice()
      .sort((a, b) => a - b)
      .map((day) => DAY_OF_WEEK_LABELS[day] ?? day)
      .join(', ');
    return labels ? `по ${labels}` : OVERHEAD_RECURRENCE_TYPE_LABELS.WEEKLY.toLowerCase();
  }

  return OVERHEAD_RECURRENCE_TYPE_LABELS[recurrenceType]?.toLowerCase() ?? 'каждый день';
}
/* END_METHOD_formatRecurrenceLabel */

/* START_METHOD_buildOverheadPayload */
/* START_CONTRACT: buildOverheadPayload
 * PURPOSE: Сформировать payload накладных расходов из данных формы.
 * INPUTS: { name, hours, recurrenceType, intervalDays, daysOfWeek, roles }.
 * OUTPUTS: { API payload }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-PROJECTS
 * END_CONTRACT: buildOverheadPayload
 */
export function buildOverheadPayload({
  name,
  hours,
  recurrenceType,
  intervalDays,
  daysOfWeek,
  roles,
}) {
  const payload = {
    name: name.trim(),
    hoursPerEmployee: parseFloat(hours),
    recurrenceType,
    roleIds: roles.map((role) => role.id),
  };

  if (recurrenceType === OVERHEAD_RECURRENCE_TYPES.INTERVAL) {
    payload.intervalDays = parseInt(String(intervalDays), 10);
  } else if (recurrenceType === OVERHEAD_RECURRENCE_TYPES.WEEKLY) {
    payload.daysOfWeek = [...daysOfWeek].sort((a, b) => a - b);
  }

  return payload;
}
/* END_METHOD_buildOverheadPayload */

/* START_METHOD_formatOverheadPreviewLabel */
/* START_CONTRACT: formatOverheadPreviewLabel
 * PURPOSE: Построить строку превью записи оверхеда для формы создания/редактирования.
 * INPUTS: { name, hours, recurrenceType, intervalDays, daysOfWeek, roles }.
 * OUTPUTS: { string }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-PROJECTS
 * END_CONTRACT: formatOverheadPreviewLabel
 */
export function formatOverheadPreviewLabel({
  name,
  hours,
  recurrenceType,
  intervalDays,
  daysOfWeek,
  roles,
}) {
  const title = name?.trim() || 'Название записи';
  const hoursText = isOverheadHoursValid(hours)
    ? `${parseFloat(hours)} ч`
    : '— ч';
  const recurrenceText = isRecurrenceValid({ recurrenceType, intervalDays, daysOfWeek })
    ? formatRecurrenceLabel({ recurrenceType, intervalDays, daysOfWeek })
    : 'периодичность не задана';
  const roleLabels = (roles ?? [])
    .map((role) => role.shortName || role.name)
    .filter(Boolean);
  const rolesText = roleLabels.length ? roleLabels.join(', ') : '—';
  return `${title}: ${hoursText}, ${recurrenceText} — ${rolesText}`;
}
/* END_METHOD_formatOverheadPreviewLabel */
